import {Badge, Button, Modal} from '@mantine/core';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React, {useState} from 'react';
import type {AccountMembershipResponse} from 'src/api/ProactApiClient';
import {RoleEnum, UserStatusEnum} from 'src/api/ProactApiClient';
import {IconSize, UserIcon} from 'src/design/atoms/icons/CommonIcons';
import {PageHeader, PageHeaderAction} from 'src/design/organisms/PageHeader';
import {DataList} from 'src/design/organisms/DataList';
import {
  useGetAccountMembershipsQuery,
  useTransferPrimaryMutation,
} from 'src/redux/queries/userInstance';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppSelector} from 'src/redux/hooks';

const ROLE_LABELS: Partial<Record<RoleEnum, string>> = {
  [RoleEnum.InstanceAdmin]: 'Admin',
  [RoleEnum.InstanceUser]: 'User',
  [RoleEnum.InstanceObserver]: 'Observer',
  [RoleEnum.InstanceSupervisor]: 'Supervisor',
  [RoleEnum.SafetyRepresentative]: 'Safety Rep',
  [RoleEnum.NotificationOnly]: 'Notifications Only',
};

const STATUS_COLORS: Partial<Record<UserStatusEnum, string>> = {
  [UserStatusEnum.Active]: 'green',
  [UserStatusEnum.Pending]: 'yellow',
  [UserStatusEnum.Inactive]: 'gray',
};

export interface InstanceAdministratorListProps {
  instanceId?: number;
  accountId?: number;
  titleText?: string;
  readOnly?: boolean;
}

export const InstanceAdministratorList: React.FC<
  InstanceAdministratorListProps
> = ({accountId, titleText, readOnly = false}) => {
  const {isSuperAdmin} = useAppSelector(state => state.session);
  const {setAlert} = useAlert();
  const [confirmTransfer, setConfirmTransfer] =
    useState<AccountMembershipResponse | null>(null);

  const {
    data: rows = [],
    isLoading,
    refetch,
  } = useGetAccountMembershipsQuery(accountId!, {
    skip: accountId === undefined,
  });

  const [transferPrimary, {isLoading: isTransferring}] =
    useTransferPrimaryMutation();

  async function handleTransferPrimary(row: AccountMembershipResponse) {
    if (accountId === undefined) return;
    try {
      await transferPrimary({
        accountId,
        targetInstanceId: row.instanceId,
      }).unwrap();
      setAlert('Primary instance transferred successfully.', 'success');
      setConfirmTransfer(null);
      void refetch();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }

  if (accountId === undefined) {
    return (
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Select a specific user account to view instance memberships.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader>
        <PageHeader.Title subtitle="Instance memberships for this account">
          {titleText ?? 'Instance Access'}
        </PageHeader.Title>
      </PageHeader>
      <DataList
        rowIcon={<UserIcon size={IconSize.lg} />}
        isLoading={isLoading}
        data={rows}
        meta={undefined}
        currentPage={1}
        totalRecords={rows.length}
        recordsPerPage={rows.length || 10}
        onPageChange={() => {}}
        onRowClick={() => {}}
        onRowTextRender={(row: AccountMembershipResponse) =>
          row.instanceName ?? `Instance ${row.instanceId}`
        }
        onRenderAfterRowNode={(row: AccountMembershipResponse) => (
          <Group gap="xs" wrap="nowrap">
            <Badge
              color={STATUS_COLORS[row.active] ?? 'gray'}
              variant="light"
              size="sm">
              {row.active}
            </Badge>
            <Badge variant="outline" size="sm" color="blue">
              {ROLE_LABELS[row.roleId] ?? row.roleId}
            </Badge>
            {row.isPrimaryInstance && (
              <Badge variant="filled" size="sm" color="teal">
                Primary
              </Badge>
            )}
            {!readOnly && isSuperAdmin && !row.isPrimaryInstance && (
              <PageHeaderAction
                variant="edit"
                onClick={() => setConfirmTransfer(row)}>
                Set Primary
              </PageHeaderAction>
            )}
          </Group>
        )}
        hasSearchTerm={false}
        hasCreationLogic={false}
      />

      {!readOnly && (
        <Modal
          opened={confirmTransfer !== null}
          onClose={() => setConfirmTransfer(null)}
          title="Transfer Primary Instance"
          size="md">
          <Stack gap="md">
            <Text size="sm">
              Transfer primary instance to{' '}
              <strong>
                {confirmTransfer?.instanceName ??
                  `Instance ${confirmTransfer?.instanceId}`}
              </strong>
              ? This will make this instance the account's primary company.
            </Text>
            <Divider />
            <Group justify="flex-end">
              <Button
                size="sm"
                variant="subtle"
                color="black"
                onClick={() => setConfirmTransfer(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                loading={isTransferring}
                onClick={() =>
                  confirmTransfer && handleTransferPrimary(confirmTransfer)
                }>
                Confirm Transfer
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </Stack>
  );
};
